/**
 * Session Context - Manages session timeout and provides user-friendly session expiration handling
 *
 * Problem Solved:
 * - Users would work for extended periods, only to discover their session expired when they hit save
 * - This caused data loss and frustration
 *
 * Solution:
 * - Track user activity (clicks, key presses, scrolls)
 * - Show warning modal 2 minutes before expected session timeout
 * - Allow user to extend session or save work
 * - Handle expired sessions gracefully with clear messaging
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

import { PUBLIC_ROUTES } from '~c/components/public-routes';

// List of public route prefixes where we should skip session tracking
const PUBLIC_ROUTE_PREFIXES = Object.values(PUBLIC_ROUTES).map((route) =>
  route.split(':')[0].replace(/\/$/, '')
);

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

// Session timing configuration (in milliseconds)
const SESSION_CONFIG = {
  // Access token expires in 10-15 minutes (configured on server)
  // We'll warn 2 minutes before the expected timeout
  // Keep this aligned with server-side access token expiry (default.js: 30m).
  INACTIVITY_WARNING_MS: 28 * 60 * 1000, // 28 minutes of inactivity triggers warning
  WARNING_COUNTDOWN_MS: 2 * 60 * 1000, // 2 minutes to respond to warning
  ACTIVITY_CHECK_INTERVAL_MS: 30 * 1000, // Check activity every 30 seconds
  SESSION_CHECK_INTERVAL_MS: 60 * 1000, // Check session validity every 60 seconds
};

interface SessionContextValue {
  /** Whether the session warning modal is visible */
  isWarningVisible: boolean;
  /** Seconds remaining before session expires (when warning is shown) */
  secondsRemaining: number;
  /** Whether the session has expired */
  isSessionExpired: boolean;
  /** Message to show when session expires */
  expirationMessage: string;
  /** Extend the session (resets activity timer) */
  extendSession: () => Promise<void>;
  /** Dismiss the warning (user acknowledged it) */
  dismissWarning: () => void;
  /** Record user activity (call this on user interactions) */
  recordActivity: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
}) => {
  // State
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(120);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [expirationMessage, setExpirationMessage] = useState('');

  // Refs to track activity without causing re-renders
  const lastActivityRef = useRef<number>(Date.now());
  const warningStartTimeRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Record user activity
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    // If warning is showing and user is active, they're still there
    // Don't auto-dismiss warning - let them explicitly extend
  }, []);

  // Extend session by making a lightweight authenticated request
  const extendSession = useCallback(async () => {
    try {
      // Make a simple authenticated request to refresh the session
      // This will trigger the server to refresh the access token
      const response = await fetch('/auth/user-profile/me', {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok || response.status === 403) {
        // 403 means user exists but can't access that profile - session is still valid
        // Session extended successfully
        lastActivityRef.current = Date.now();
        warningStartTimeRef.current = null;
        setIsWarningVisible(false);
        setSecondsRemaining(120);

        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      } else if (response.status === 401) {
        // Session actually expired
        handleSessionExpired('Your session has expired. Please log in again.');
      }
    } catch {
      // Network error - assume session is still valid
      // User can try again
      lastActivityRef.current = Date.now();
      setIsWarningVisible(false);
    }
  }, []);

  // Dismiss warning without extending
  const dismissWarning = useCallback(() => {
    setIsWarningVisible(false);
    // Don't reset warning start time - warning will reappear if still inactive
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback((message: string) => {
    setIsSessionExpired(true);
    setExpirationMessage(message);
    setIsWarningVisible(false);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    const isAlreadyOnSignIn = window.location.pathname.startsWith('/sign-in');

    // Don't redirect if already on sign-in page (prevents infinite loop)
    if (isAlreadyOnSignIn) {
      return;
    }

    setTimeout(() => {
      const currentPath = window.location.pathname;
      const qs = new URLSearchParams({ referrer: currentPath });
      window.location.href = `/sign-in?${qs.toString()}`;
    }, 1000);
  }, []);

  // Start countdown timer when warning is shown
  const startCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    warningStartTimeRef.current = Date.now();
    setSecondsRemaining(Math.floor(SESSION_CONFIG.WARNING_COUNTDOWN_MS / 1000));

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - (warningStartTimeRef.current ?? Date.now());
      const remaining = Math.max(
        0,
        Math.floor((SESSION_CONFIG.WARNING_COUNTDOWN_MS - elapsed) / 1000)
      );
      setSecondsRemaining(remaining);

      if (remaining <= 0) {
        handleSessionExpired(
          'Your session has timed out due to inactivity. Please log in again.'
        );
      }
    }, 1000);
  }, [handleSessionExpired]);

  // Check for inactivity and show warning
  useEffect(() => {
    const checkActivity = () => {
      // Don't check if already expired or warning is showing
      if (isSessionExpired) {
        return;
      }

      const now = Date.now();
      const inactiveDuration = now - lastActivityRef.current;

      // If inactive for too long, show warning
      if (
        inactiveDuration >= SESSION_CONFIG.INACTIVITY_WARNING_MS &&
        !isWarningVisible
      ) {
        setIsWarningVisible(true);
        startCountdown();
      }
    };

    const intervalId = setInterval(
      checkActivity,
      SESSION_CONFIG.ACTIVITY_CHECK_INTERVAL_MS
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [isWarningVisible, isSessionExpired, startCountdown]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      recordActivity();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [recordActivity]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Check if we're on a public page (don't track session there)
  // Use PUBLIC_ROUTES constant to ensure consistency across the app
  const isPublicPage =
    typeof window !== 'undefined' && isPublicRoute(window.location.pathname);

  // Reset session expired state when returning from public pages (e.g., after re-login)
  useEffect(() => {
    if (!isPublicPage && isSessionExpired) {
      // User has successfully logged back in, reset the expired state
      setIsSessionExpired(false);
      setExpirationMessage('');
      lastActivityRef.current = Date.now();
    }
  }, [isPublicPage, isSessionExpired]);

  // Don't provide session management on public pages
  if (isPublicPage) {
    return <>{children}</>;
  }

  const value: SessionContextValue = {
    isWarningVisible,
    secondsRemaining,
    isSessionExpired,
    expirationMessage,
    extendSession,
    dismissWarning,
    recordActivity,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

/**
 * Hook to access session context
 */
export const useSessionContext = (): SessionContextValue => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
};

/**
 * Optional hook that returns null if not in a SessionProvider
 * Useful for components that might be used in both authenticated and public contexts
 */
export const useSessionContextOptional = (): SessionContextValue | null => {
  return useContext(SessionContext);
};

export default SessionProvider;
